import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { GROUPBY } from '../../../constants/Reports';
import SelectRebuild from '../../../components/Inputs/SelectRebuild';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import ReportsHeader from '../../../components/Reports/ReportsHeader';
import { FilterPanel } from '../../../components/Reports/FilterPanel';
import { useForm } from 'react-hook-form';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import {
  selectEmployeeData,
  selectWoburnMunisPtoCSVReport,
  selectWoburnMunisPtoReport,
} from '../../../store/ReportsDetailsSlice/selectors';
import {
  getEmployeeList,
  getWoburnMunisPtoCSVReportCsvData,
  getWoburnMunisPtoReport,
  setWoburnMunisPtoCSVReportData,
} from '../../../store/ReportsDetailsSlice/actions';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { useColumns } from './hooks/useColumns';
import { useRows } from './hooks/useRows';
import DataNotFound from '../../../components/Reports/DataNotFound';
import ReusableGrid from '../../../components/Grid';

const WoburnMunisPtoReport = () => {
  const dispatch = useDispatch();
  const { woburnMunisPtoData, isWoburnMunisPtoDataLoading } = useSelector(
    selectWoburnMunisPtoReport
  );
  const { woburnMunisPtoCSVData, isWoburnMunisPtoCSVLoading } = useSelector(
    selectWoburnMunisPtoCSVReport
  );
  const { employeeList, loadingEmployeeList } = useSelector(selectEmployeeData);

  const { register, setValue, watch } = useForm({
    defaultValues: {
      emp_code: [],
      groupby: { id: GROUPBY[0].value, name: GROUPBY[0].title },
    },
  });

  const { fromDate, toDate, render } = useDateRangeFilter();

  const { emp_code: employees, groupby } = watch();

  const param = useMemo(() => {
    return {
      start_dt: moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00',
      end_dt: moment(toDate).format('YYYY-MM-DD') + ' 23:59:59',
      sel_order: groupby.id,
      sel_emp:
        employees.length === 0 ? undefined : employees.map((item) => item.id),
    };
  }, [fromDate, toDate, groupby, employees]);

  const handleDownloadReport = () => {
    const query = { ...param, csv: 1 };
    dispatch(getWoburnMunisPtoCSVReportCsvData(query));
  };

  const handleFindClick = () => dispatch(getWoburnMunisPtoReport(param));

  useEffect(() => {
    dispatch(getEmployeeList());
    dispatch(getWoburnMunisPtoReport(param));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (woburnMunisPtoCSVData.data) {
      saveCsvToFile(woburnMunisPtoCSVData.data, woburnMunisPtoCSVData.filename);
      dispatch(setWoburnMunisPtoCSVReportData({ data: null, filename: '' }))
    }
  }, [woburnMunisPtoCSVData, dispatch]);

  const columns = useColumns();
  const rows = useRows(woburnMunisPtoData.data);

  const rawFilterConfig = [
    {
      key: 'group_by',
      label: 'Group By',
      show: GROUPBY.length > 0,
      renderComponent: () => (
        <SelectRebuild
          options={GROUPBY.map((item) => ({
            id: item.value,
            name: item.title,
          }))}
          name="groupby"
          label="Group By"
          multiple={false}
          loading={false}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'emp_code',
      label: 'Employee',
      show: true,
      renderComponent: () => (
        <SelectRebuild
          options={employeeList}
          name="emp_code"
          label="Employee"
          multiple
          allOption={{ id: 'ALL', name: 'All' }}
          loading={loadingEmployeeList}
          register={register}
          setValue={setValue}
          watch={watch}
        />
      ),
    },
    {
      key: 'find_button',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <Button
            variant="contained"
            onClick={handleFindClick}
            sx={{ whiteSpace: 'nowrap', height: '32px' }}
          >
            Find
          </Button>
        </Grid>
      ),
    },
    {
      key: 'export',
      label: '',
      show: true,
      renderComponent: () => (
        <Grid
          item
          sx={{
            width: '100%',
            display: 'flex',
            gap: 2,
            justifyContent: 'end',
            alignItems: 'center',
            height: '100%',
            paddingTop: '23px',
          }}
        >
          <ExportCsvButton
            onClick={handleDownloadReport}
            disabled={isWoburnMunisPtoCSVLoading}
            text={'Export CSV'}
          />
        </Grid>
      ),
    },
  ];

  const filterConfig = rawFilterConfig.filter((f) => f.show !== false);
  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Woburn Munis PTO Report Details" />

      <Box className="mt-4 mb-4" sx={{ width: '100%' }}>
        <Grid container sx={{ width: '100%', mb: '24px' }}>
          <FilterPanel filters={filterConfig} columns={4} />
        </Grid>

        <Grid container sx={{ width: '100%' }}>
          <Grid
            item
            sx={{
              display: 'flex',
              gap: 2,
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Grid
              item
              sx={{ display: 'flex', gap: 2, width: '100%', mt: 'auto' }}
            >
              {render}
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {isWoburnMunisPtoDataLoading ? (
        <Box
          sx={{ minHeight: '30px' }}
          className="w-10 h-10 d-flex justify-content-center align-items-center"
        >
          <CircularProgress size={32} />
        </Box>
      ) : woburnMunisPtoData.length === 0 ? (
        <DataNotFound />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          {rows.map((item) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {item.title && (
                <Typography fontWeight={600} variant="h6">
                  {item.title}
                </Typography>
              )}
              <ReusableGrid columns={columns} rows={item.items} />
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default WoburnMunisPtoReport;
